import React, { useContext, useEffect, useMemo, useRef } from 'react';
import noop from 'lodash/noop';
import { usePathname } from '@framework/next/navigation';
import Input, { InputRef } from '@creditflowresearch/uilib/Input';
import useResponsive from '@creditflowresearch/uilib/hooks/useResponsive';
import { useHeaderMenuContext } from '@/creditflowresearch/application/SearchHeaderMenu/HeaderMenuContext';
import { HEADER_SEARCH_INPUT_ID } from '@/creditflowresearch/application/SearchHeaderMenu/constants';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useHeaderMenuContext as useHeaderMenuContextV2 } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import IconSearch from '@creditflowresearch/uilib/Icon/IconSearch';

import styles from './searchInput.module.scss';

interface IProps {
  value: string,
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    products: IProduct[],
    regions: IRegion[],
  ) => void,
}

const SearchInput = ({
  value,
  onChange = noop,
}: IProps) => {
  const pathname = usePathname();
  const legacy = useContext(DictionariesContext);
  const { products: v2Products, regions: v2Regions } = useHeaderMenuContextV2();
  const productsList = legacy?.productsList ?? v2Products;
  const regionsList = legacy?.regionsList ?? v2Regions;
  const {
    products: accessibleProductsIds,
    regions: accessibleRegionsIds,
  } = useUserPlanContext();
  const isMobile = useResponsive(({ width }) => width < 1024);

  const accessibleProducts = useMemo(
    () => productsList.filter((product: IProduct) => accessibleProductsIds.includes(product.id)),
    [productsList, accessibleProductsIds],
  );
  const accessibleRegions = useMemo(
    () => regionsList.filter((region: IRegion) => accessibleRegionsIds.includes(region.id)),
    [regionsList, accessibleRegionsIds],
  );

  const {
    setIsSearchResultsOpen,
  } = useHeaderMenuContext();

  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    if (pathname === '/' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [pathname]);

  const handlerShowResults = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setIsSearchResultsOpen(!!ev?.target?.value);
  };

  const handlerOnClick = (ev: React.MouseEvent<HTMLInputElement>) => {
    setIsSearchResultsOpen(!!(ev.target as HTMLInputElement)?.value);
  };

  const handlerOnChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setIsSearchResultsOpen(!!ev.target.value);
    onChange(ev, accessibleProducts, accessibleRegions);
  };

  return (
    <div className={ styles.searchContainer }>
      <Input
        value={ value }
        placeholder="Search: Parents, Issuers or Deals"
        ref={ inputRef }
        name="headerSearchInput"
        dataTest="headerSearchInput"
        type="text"
        icon={ IconSearch }
        onChange={ handlerOnChange }
        onFocus={ handlerShowResults }
        onClick={ handlerOnClick }
        // @ts-ignore
        input={ { id: HEADER_SEARCH_INPUT_ID } }
        isNarrow
        size={ isMobile ? 'S' : 'M' }
      />
    </div>
  );
};

export default SearchInput;
